import "../assets/global.css";
import axios from "axios";
import useAuth from "../hooks/useAuth";
import { useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MEMBERSHIP_ALIASES = {
	expense: ["expense", "expenses"],
	expenses: ["expense", "expenses"],
	loganalyzer: ["logAnalyzer"],
	reportsapp: ["reportsApp"],
	purchaseorders: ["purchaseOrders"],
	userviewer: ["userViewer"],
	training: ["training"],
};

const normalizeMembershipKey = key =>
	String(key || "")
		.trim()
		.toLowerCase()
		.replace(/[\s_-]/g, "");

export const normalizeAppMemberships = memberships =>
	Object.entries(memberships || {}).reduce((acc, [key, value]) => {
		const cleanKey = String(key || "").trim();
		if (!cleanKey) return acc;
		acc[cleanKey] = value;
		const aliases = MEMBERSHIP_ALIASES[normalizeMembershipKey(cleanKey)] || [];
		aliases.forEach(alias => {
			acc[alias] = value;
		});
		return acc;
	}, {});

export const getAppMemberships = authData =>
	normalizeAppMemberships(authData?.appMemberships || authData?.roles?.permissions || {});

export const getPostLoginPath = authData => {
	const memberships = getAppMemberships(authData);
	const activeApps = Object.entries(memberships || {})
		.filter(([, role]) => {
			const normalizedRole = String(role || "").trim().toLowerCase();
			return normalizedRole && normalizedRole !== "inactive" && normalizedRole !== "disabled";
		})
		.map(([appName]) => String(appName || "").trim().toLowerCase());

	return activeApps.length === 1 && activeApps[0] === "training"
		? "/training/calendar"
		: "/dashboard";
};

function Login() {
	//const { login } = useAuth();
	const { setAuth } = useAuth();
	const navigate = useNavigate();
	const [forgotOpen, setForgotOpen] = useState(false);
	const [forgotEmail, setForgotEmail] = useState("");
	const [forgotLoading, setForgotLoading] = useState(false);

	const userPermission = (authData, key) => getAppMemberships(authData)?.[key] === "admin";
	const userPermissionNoBoolean = (authData, key) => getAppMemberships(authData)?.[key] || "user";

	const handleLogin = async event => {
		event.preventDefault();

		const userEmail = event.target.userEmail.value;
		const userPassword = event.target.userPassword.value;
		const endpoint = process.env.REACT_APP_API_URL + "login";

		try {
			const response = await axios.post(
				endpoint,
				{
					email: userEmail,
					password: userPassword,
				},
				{
					withCredentials: true,
					headers: {
						"Content-Type": "application/json",
					},
				}
			);
			const payload = response?.data || {};
			const isGeofenceLogin = !!payload?.token && !!payload?.user;

			if (payload.userType === "user") {
				//history("/comunidades");
			} else if (payload.userType === "admin") {
				//history("/dashboard");
			}

			const authData = isGeofenceLogin
				? {
						accessToken: payload.token,
						userId: payload.user.id,
						userName: payload.user.fullName,
						email: payload.user.email,
						roles: {
							userType: payload.user.role || "user",
							permissions: {
								geofence: payload.user.role || "user",
							},
						},
					}
				: payload;

			setAuth(authData);
			localStorage.setItem("appMemberships", JSON.stringify(getAppMemberships(authData)));
			localStorage.setItem("userName", authData?.userName || "");
			localStorage.setItem("userEmail", authData?.email || "");
			localStorage.setItem("userId", authData?.userId || "");
			localStorage.setItem("userType", authData?.roles?.userType || "user");
			localStorage.setItem("permissions.training", userPermissionNoBoolean(authData, "training"));
			localStorage.setItem("permissions.purchaseOrders", userPermissionNoBoolean(authData, "purchaseOrders"));
			localStorage.setItem("clientId", authData?.clientId || "");
			localStorage.setItem("clientName", authData?.clientName || "");
			localStorage.setItem("clientCredits", authData?.clientCredits ?? "");
			localStorage.setItem("expenseAdmin", userPermission(authData, "expenses"));
			localStorage.setItem("userviewerAdmin", userPermission(authData, "userViewer"));
			localStorage.setItem("userImmexRole", userPermissionNoBoolean(authData, "immex"));
			localStorage.setItem("userLcuManagerRole", userPermissionNoBoolean(authData, "LCU Manager"));
			localStorage.setItem("organizationId", authData?.organization_id || "");

			if (!localStorage.getItem("theme")) {
				localStorage.setItem("theme", "light");
			}
			navigate(getPostLoginPath(authData), { replace: true });
		} catch (error) {
			console.log(error);
			const message =
				error?.response?.data?.msg ||
				error?.response?.data?.message ||
				error?.message ||
				"Login failed";
			toast.error(message);
		}
	};

	const handleForgotPassword = async e => {
		e.preventDefault();
		if (!forgotEmail.trim()) {
			toast.error("Email is required");
			return;
		}
		setForgotLoading(true);
		try {
			await axios.post(`${process.env.REACT_APP_API_URL}forgot-password`, {
				email: forgotEmail.trim(),
			});
			toast.success("If your account exists, a reset email was sent.");
			setForgotOpen(false);
			setForgotEmail("");
		} catch (error) {
			toast.error(error?.response?.data?.message || "Could not process request");
		} finally {
			setForgotLoading(false);
		}
	};

	return (
		<div
			data-testid="login-command-center"
			className="relative min-h-screen overflow-hidden bg-[#06101f] text-white"
		>
			<div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_12%,rgba(45,212,191,0.22),transparent_28%),radial-gradient(circle_at_86%_18%,rgba(59,130,246,0.20),transparent_26%),linear-gradient(135deg,#07111f,#0f2747_48%,#06101f)]" />
			<div className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-cyan-300/10 blur-3xl" />
			<div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
			<div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(255,255,255,0.8)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.8)_1px,transparent_1px)] [background-size:44px_44px]" />

			<main className="relative z-10 flex min-h-screen items-center px-5 py-8 sm:px-8 lg:px-12">
				<div className="mx-auto grid w-full max-w-7xl items-center gap-8 lg:grid-cols-[0.92fr_1.08fr]">
					<section className="rounded-[2rem] border border-white/12 bg-slate-950/68 p-6 shadow-2xl shadow-black/30 backdrop-blur-xl sm:p-8 lg:p-10">
						<div className="mb-8 flex items-center justify-between gap-4">
							<div className="rounded-2xl bg-white px-4 py-3 shadow-lg shadow-cyan-950/20">
								<img
									src="/images/pacLogoBlack.svg"
									alt="PAC Mexico"
									className="h-10 w-auto"
								/>
							</div>
							<div className="hidden rounded-full border border-cyan-200/20 bg-cyan-200/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-cyan-100 sm:block">
								Secure access
							</div>
						</div>

						<div className="space-y-3">
							<p className="text-xs font-black uppercase tracking-[0.22em] text-teal-200">
								Operations workspace
							</p>
							<h1 className="text-4xl font-black leading-[0.95] tracking-[-0.06em] text-white sm:text-5xl">
								Welcome back
							</h1>
							<p className="max-w-lg text-sm leading-6 text-slate-300 sm:text-base">
								Sign in to manage service reports, training, purchase orders and daily PAC workflows from one focused command center.
							</p>
						</div>

						<form onSubmit={handleLogin} action="/schedule" className="mt-9 space-y-5">
							<div>
								<label
									htmlFor="login-email"
									className="mb-2 block text-sm font-bold tracking-wide text-slate-200"
								>
									Email Address
								</label>
								<input
									id="login-email"
									name="userEmail"
									className="pac-login-input h-12 w-full rounded-2xl border border-white/12 bg-white/95 px-4 text-base text-slate-950 shadow-inner outline-none transition placeholder:text-slate-400 focus:border-teal-300 focus:ring-4 focus:ring-teal-300/20"
									type="email"
									required
									placeholder="name@pacmexico.com.mx"
								/>
							</div>

							<div>
								<div className="mb-2 flex items-center justify-between gap-3">
									<label
										htmlFor="login-password"
										className="block text-sm font-bold tracking-wide text-slate-200"
									>
										Password
									</label>
									<button
										type="button"
										className="text-xs font-bold uppercase tracking-[0.14em] text-teal-200 transition hover:text-white"
										onClick={() => setForgotOpen(true)}
									>
										Forgot Password?
									</button>
								</div>
								<input
									id="login-password"
									name="userPassword"
									className="pac-login-input h-12 w-full rounded-2xl border border-white/12 bg-white/95 px-4 text-base text-slate-950 shadow-inner outline-none transition placeholder:text-slate-400 focus:border-teal-300 focus:ring-4 focus:ring-teal-300/20"
									type="password"
									required
									placeholder="Enter your password"
								/>
							</div>

							<button
								type="submit"
								className="group relative h-14 w-full overflow-hidden rounded-2xl bg-gradient-to-r from-cyan-400 via-teal-300 to-blue-500 px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-slate-950 shadow-xl shadow-cyan-950/30 transition hover:-translate-y-0.5 hover:shadow-cyan-400/20 focus:outline-none focus:ring-4 focus:ring-cyan-300/30"
							>
								<span className="relative z-10">Log In</span>
								<span className="absolute inset-0 translate-x-[-100%] bg-white/35 transition duration-500 group-hover:translate-x-[100%]" />
							</button>
						</form>

						<div className="mt-7 flex flex-col gap-3 text-center text-sm font-semibold text-slate-300 sm:flex-row sm:items-center sm:justify-between sm:text-left">
							<span>Need a PAC account?</span>
							<Link
								to="/signup"
								className="rounded-full border border-white/12 px-4 py-2 font-black uppercase tracking-[0.14em] text-cyan-100 transition hover:border-cyan-200/40 hover:bg-cyan-200/10 hover:text-white"
							>
								Request access
							</Link>
						</div>
					</section>

					<section className="hidden min-h-[620px] overflow-hidden rounded-[2.25rem] border border-white/12 bg-white/[0.06] p-6 shadow-2xl shadow-black/25 backdrop-blur-xl lg:block">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-xs font-black uppercase tracking-[0.2em] text-cyan-100">
									PAC command center
								</p>
								<h2 className="mt-2 text-3xl font-black tracking-[-0.05em] text-white">
									Operational apps in one place
								</h2>
							</div>
							<div className="rounded-full border border-emerald-200/20 bg-emerald-300/10 px-3 py-1 text-xs font-bold text-emerald-100">
								System ready
							</div>
						</div>

						<div className="relative mt-8 h-[500px] rounded-[2rem] border border-white/10 bg-slate-950/48 p-6">
							<div className="absolute inset-6 rounded-[1.5rem] border border-cyan-200/10 bg-[radial-gradient(circle_at_50%_30%,rgba(45,212,191,0.16),transparent_34%)]" />
							<div className="relative grid h-full grid-cols-2 gap-4">
								<div className="rounded-3xl border border-white/10 bg-white/[0.08] p-5">
									<div className="mb-8 h-2 w-16 rounded-full bg-cyan-300/80" />
									<p className="text-sm font-bold text-slate-300">Service Reports</p>
									<p className="mt-3 text-4xl font-black tracking-[-0.08em] text-white">SRM</p>
									<p className="mt-3 text-xs leading-5 text-slate-400">Reports, PDFs, resend email and coordinator visibility.</p>
								</div>
								<div className="rounded-3xl border border-white/10 bg-white/[0.08] p-5">
									<div className="mb-8 h-2 w-16 rounded-full bg-teal-300/80" />
									<p className="text-sm font-bold text-slate-300">Training</p>
									<p className="mt-3 text-4xl font-black tracking-[-0.08em] text-white">LMS</p>
									<p className="mt-3 text-xs leading-5 text-slate-400">Courses, calendars, summaries and participant history.</p>
								</div>
								<div className="rounded-3xl border border-white/10 bg-white/[0.08] p-5">
									<div className="mb-8 h-2 w-16 rounded-full bg-amber-300/80" />
									<p className="text-sm font-bold text-slate-300">Purchase Orders</p>
									<p className="mt-3 text-4xl font-black tracking-[-0.08em] text-white">PO</p>
									<p className="mt-3 text-xs leading-5 text-slate-400">Link reports, review totals and track approvals.</p>
								</div>
								<div className="rounded-3xl border border-white/10 bg-white/[0.08] p-5">
									<div className="mb-8 h-2 w-16 rounded-full bg-rose-300/80" />
									<p className="text-sm font-bold text-slate-300">Service Desk</p>
									<p className="mt-3 text-4xl font-black tracking-[-0.08em] text-white">SHD</p>
									<p className="mt-3 text-xs leading-5 text-slate-400">Tickets, action trackers and field work visibility.</p>
								</div>
							</div>
						</div>
					</section>
				</div>
			</main>

			<ToastContainer
				position="bottom-right"
				autoClose={5000}
				hideProgressBar={false}
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
			/>

			{forgotOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center px-4">
					<button
						type="button"
						aria-label="Close forgot password dialog"
						className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
						onClick={() => setForgotOpen(false)}
					/>
					<div className="relative w-full max-w-md rounded-[1.75rem] border border-white/12 bg-slate-950/90 p-6 text-white shadow-2xl shadow-black/40 backdrop-blur-xl">
						<div className="rounded-2xl bg-cyan-200/10 px-4 py-3">
							<div className="text-xs font-black uppercase tracking-[0.18em] text-cyan-100">
								Account recovery
							</div>
							<div className="mt-2 text-2xl font-black tracking-[-0.04em]">Forgot password</div>
							<div className="mt-1 text-sm leading-6 text-slate-300">
								Enter your email and we will send you a reset link.
							</div>
						</div>
						<form onSubmit={handleForgotPassword} className="mt-5 space-y-4">
							<label htmlFor="forgot-email" className="sr-only">
								Recovery email
							</label>
							<input
								id="forgot-email"
								type="email"
								className="h-12 w-full rounded-2xl border border-white/12 bg-white/95 px-4 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-teal-300 focus:ring-4 focus:ring-teal-300/20"
								placeholder="name@pacmexico.com.mx"
								value={forgotEmail}
								onChange={e => setForgotEmail(e.target.value)}
							/>
							<div className="flex justify-end gap-2">
								<button
									type="button"
									className="rounded-2xl border border-white/12 px-4 py-2 text-sm font-bold text-slate-200 transition hover:bg-white/10"
									onClick={() => setForgotOpen(false)}
								>
									Cancel
								</button>
								<button
									type="submit"
									disabled={forgotLoading}
									className="rounded-2xl bg-gradient-to-r from-cyan-300 to-teal-300 px-4 py-2 text-sm font-black text-slate-950 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
								>
									{forgotLoading ? "Sending..." : "Send reset link"}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}

export default Login;
